import { z } from 'zod';
import { FLEET_ACCOUNT_LIMITS, FleetAccountSchema, FleetProfileStatusSchema } from './fleet-status.schemas';
import { FleetAccountSettingsSchema } from './fleet-registered-accounts.schemas';
import { FleetHostHealthSchema } from './fleet-health.schemas';

/**
 * The tick's ceilings (IMM-914.3). A host may LENGTHEN its own interval — a
 * metered or flaky connection is its own problem to manage — but the cap on a
 * single delivery is the backend's, so one misbehaving host cannot shape the
 * table's write load on its own.
 */
export const FLEET_TICK_INTERVAL_MS = 10_000;
export const FLEET_TICK_MAX_LINES = 200;
export const FLEET_LOG_LINE_MAX_CHARS = 2_000;
export const FLEET_TICK_MAX_RECORDS = 8;
export const FLEET_RECORD_MAX_CHARS = 64_000;
export const FLEET_RECORD_SCHEMA_VERSION = 1;
/** One entry per lane on the host — the same ceiling as `profiles`. */
export const FLEET_TICK_MAX_LIVE_JOBS = 32;

/**
 * What a STORED line can be — deliberately its own vocabulary, not the `logs`
 * verb's `build | daemon | pane`.
 *
 * The two answer different questions. The verb's list describes what a machine
 * can be asked to read on demand, and `pane` belongs to it because a terminal
 * buffer can be captured but has no offset to resume from. This list describes
 * what the fleet agent pushes continuously and the dashboard reads back, so it
 * names things the way an operator does:
 *
 * - `agent` — the lane's own decision log, which is what its tmux session
 *   shows. Bounded, and the stream that answers "why is this agent idle".
 * - `build` — the spawned engine's stdout.
 * - `event` — reserved for the structured run events the ledger already keeps.
 *
 * Keeping them separate is what lets the `logs` verbs retire without the
 * stored rows having to be rewritten.
 */
export const FLEET_LOG_LINE_STREAMS = ['agent', 'build', 'event'] as const;
export const FleetLogLineStreamSchema = z.enum(FLEET_LOG_LINE_STREAMS);
export type FleetLogLineStream = (typeof FLEET_LOG_LINE_STREAMS)[number];

/**
 * One collected line. `seq` is the worker's own monotonic counter, and it is
 * what distinguishes two identical lines a millisecond apart — a content hash
 * would collapse them, which is wrong for build output.
 */
export const FleetLogLineSchema = z.object({
  /**
   * Which worker produced it. On the LINE rather than on the body: the host
   * component reports every profile on the box at once, and those profiles are
   * different workers — a single body-level worker could only ever be right
   * for one of them.
   */
  worker: z.string().min(1).max(128),
  seq: z.number().int().nonnegative(),
  at: z.iso.datetime(),
  stream: FleetLogLineStreamSchema,
  text: z.string(),
});

export type FleetLogLine = z.infer<typeof FleetLogLineSchema>;

export const FLEET_LEDGER_RECORD_KINDS = ['run', 'session'] as const;

/**
 * `payload` is the record as JSON TEXT, not a nested object: a record whose
 * SHAPE is wrong still parses as a valid envelope, so one bad entry is refused
 * on its own instead of failing the whole body.
 */
export const FleetLedgerRecordSchema = z.object({
  kind: z.enum(FLEET_LEDGER_RECORD_KINDS),
  /** OPAQUE to the backend: an acknowledgement token the agent chose, echoed back unread. */
  id: z.string().min(1).max(200),
  payload: z.string().min(1).max(FLEET_RECORD_MAX_CHARS),
});

export type FleetLedgerRecord = z.infer<typeof FleetLedgerRecordSchema>;

export const FLEET_LEDGER_REFUSAL_REASONS = ['schema', 'unsupported-version', 'worker-mismatch'] as const;

export const FleetLedgerRefusalSchema = z.object({
  id: z.string(),
  reason: z.enum(FLEET_LEDGER_REFUSAL_REASONS),
  detail: z.string().max(500).optional(),
});

export type FleetLedgerRefusal = z.infer<typeof FleetLedgerRefusalSchema>;

/**
 * `worker` is OPTIONAL and names only the worker that holds `job` — the host
 * component that ticks for liveness is not itself a worker and holds nothing.
 * Log lines carry their own worker, because one tick reports every profile on
 * the box and those are different workers.
 */
export const FleetTickBodySchema = z.object({
  worker: z.string().min(1).max(128).optional(),
  checkoutCommit: z.string().max(200).optional(),
  /**
   * The fleet-agent release installed on the host (e.g. "0.21.0"), sent on
   * every tick from 0.21.0 on. Absent from an older host, which leaves the
   * version it enrolled with standing.
   */
  agentVersion: z.string().min(1).max(32).optional(),
  job: z.object({ jobId: z.uuid(), claimEpoch: z.number().int().positive() }).optional(),
  profiles: z.array(FleetProfileStatusSchema).max(32),
  lines: z.array(FleetLogLineSchema).optional(),
  records: z.array(FleetLedgerRecordSchema).optional(),
  /** The engine accounts the host's lanes run on, each tick; the backend keeps the latest on the machine. */
  accounts: z.array(FleetAccountSchema).max(FLEET_ACCOUNT_LIMITS.accounts).optional(),
  /** Absent from a host that predates IMM-1450, which leaves its stored readings alone. */
  health: FleetHostHealthSchema.optional(),
  /**
   * The job heartbeat: every job a lane on this host is running right now.
   * Each entry whose holder is this machine at this epoch has its `lastBeatAt`
   * moved to now; any other entry is ignored, never refused. Absent means a
   * host that predates the heartbeat — its jobs keep a null `lastBeatAt` and
   * the watchdog never reaps them.
   */
  liveJobs: z
    .array(
      z.object({
        jobId: z.uuid(),
        claimEpoch: z.number().int().positive(),
        /** The last line a command of the round reported (fleet-agent ADR 0012). Overwrites the stored one. */
        progress: z.object({ at: z.iso.datetime(), line: z.string().max(200) }).optional(),
        /**
         * A review judged the round stuck (fleet-agent ADR 0012). Sent until the round ends; a flag whose `since` is
         * not after the operator's last clear of the job is ignored.
         */
        suspect: z.object({ since: z.iso.datetime(), reason: z.string().max(500) }).optional(),
        /**
         * The newest write to the round's own task log, heartbeats included (fleet-agent ADR 0019): the task is
         * alive, judged from its log rather than from the host that ticks for it. Overwrites the stored one.
         */
        logAt: z.iso.datetime().optional(),
      }),
    )
    .max(FLEET_TICK_MAX_LIVE_JOBS)
    .optional(),
});

export type FleetTickBody = z.infer<typeof FleetTickBodySchema>;

export const FLEET_CLAIM_LOST_REASONS = ['ended', 'released', 'superseded'] as const;
export const FleetClaimLostReasonSchema = z.enum(FLEET_CLAIM_LOST_REASONS);
export type FleetClaimLostReason = (typeof FLEET_CLAIM_LOST_REASONS)[number];

/**
 * `reason` is absent when the body named no job at all — that is not an error,
 * and the schema must permit it rather than demanding a reason the producer
 * has none of (BL-039).
 */
export const FleetTickClaimSchema = z.object({
  held: z.boolean(),
  jobId: z.uuid().optional(),
  reason: FleetClaimLostReasonSchema.optional(),
});

export type FleetTickClaim = z.infer<typeof FleetTickClaimSchema>;

/** `commands` ships from IMM-914.3 always empty; IMM-914.4 fills it. */
export const FleetTickCommandSchema = z.object({
  id: z.uuid(),
  verb: z.string(),
  profile: z.string().nullable(),
  stream: z.string().nullable(),
  tailLines: z.number().int().nullable(),
  /**
   * `null` for every verb but `handover` (OPS-29). Optional on the wire so a
   * host on the new agent still reads a backend that predates the field.
   */
  ticket: z.string().nullish(),
  /** Only on `stop` / `stop-all` (fleet-agent ADR 0013): after the round in hand. Optional for an older backend. */
  finish: z.boolean().nullish(),
  /**
   * Only on `engine` (fleet-agent 0.19.0): the engine (`null` unsets), the lane it overrides (absent: the
   * default), the one project that override is narrowed to, and the models. `project` also narrows a `start` /
   * `stop` / `restart` (ADR 0013), an `e2e-unlock` (IMM-1153), a `run-command` (ADR 0016) and a `system-preview` /
   * `system-prune` (ADR 0018) to one project.
   */
  engine: z.string().nullish(),
  lane: z.string().nullish(),
  project: z.string().nullish(),
  planModel: z.string().nullish(),
  implementModel: z.string().nullish(),
  /**
   * Only on `steer` (OPS-55): the job and the worker holding it, the message, whether it is a bare `stop`, and who
   * sent it — what the host writes into that worker's steer inbox as `[steering from <author>] <message>`.
   * `worker` also names whose e2e stack an `e2e-unlock` (IMM-1153) unlocks (`null`: the host's manual stack).
   */
  jobId: z.string().nullish(),
  worker: z.string().nullish(),
  message: z.string().nullish(),
  stop: z.boolean().nullish(),
  author: z.string().nullish(),
  /** Only on `run-command` (fleet-agent ADR 0016): the words to run. Checked again on the host before they run. */
  argv: z.array(z.string()).nullish(),
  /** Only on `agent` (ADR 0070): the host verb and its flat args. The host checks both against its own table. */
  action: z.string().nullish(),
  args: z.record(z.string(), z.unknown()).nullish(),
});

export type FleetTickCommand = z.infer<typeof FleetTickCommandSchema>;

/**
 * One capped profile as the fleet stands, seen from the ticking machine
 * (IMM-979). `runningOn` names the OTHER machines running it, by machine name —
 * it is read by a person at a terminal, not joined against anything.
 */
export const FleetTickCapacitySchema = z.object({
  profile: z.string(),
  maxWorkers: z.number().int().positive(),
  runningOn: z.array(z.string()),
});

export type FleetTickCapacity = z.infer<typeof FleetTickCapacitySchema>;

export const FleetTickResponseSchema = z.object({
  claim: FleetTickClaimSchema,
  acceptedLines: z.number().int().nonnegative(),
  acceptedRecords: z.array(z.string()),
  refusedRecords: z.array(FleetLedgerRefusalSchema),
  commands: z.array(FleetTickCommandSchema),
  /**
   * Every capped profile, whatever this machine is running. Optional so an
   * agent built before IMM-979 still parses a newer backend's answer, and a
   * newer agent an older backend's — absent means "no snapshot", never "free".
   */
  capacity: z.array(FleetTickCapacitySchema).optional(),
  /** Always `null` since IMM-1450, so a host no longer reads fresh usage for it before a claim. Absent for an unapproved machine. */
  weeklyLimitPercent: z.number().int().min(1).max(100).nullish(),
  /**
   * The operator's settings for every account this machine holds (fleet-agent ADR 0027), by engine + e-mail. An
   * account missing from it has none: on, no weekly cap. Absent for an unapproved machine and from an older backend.
   */
  accountSettings: z.array(FleetAccountSettingsSchema).optional(),
});

export type FleetTickResponse = z.infer<typeof FleetTickResponseSchema>;

/**
 * `POST /v1/fleet/commands/take` (OPS-57): the commands waiting for this host,
 * marked sent in the same statement — the tick's `commands` without the rest
 * of the report. Signed with the `tick` purpose: it hands out exactly what a
 * tick would, so it grants nothing a captured tick signature does not.
 */
export const TakeFleetCommandsResponseSchema = z.object({
  commands: z.array(FleetTickCommandSchema),
});

export type TakeFleetCommandsResponse = z.infer<typeof TakeFleetCommandsResponseSchema>;

/**
 * Reading the fleet's stored log lines (IMM-918).
 *
 * Deliberately NOT a command. The fleet agent pushes lines on every tick, so
 * the dashboard reads rows that are already here rather than asking a machine
 * to go and look — which is what made a log view depend on a reachable host,
 * a live tmux session, and a round trip that could be acknowledged and never
 * answered.
 */
export const FLEET_LOG_READ_LIMITS = [10, 50, 100] as const;
export const FleetLogReadLimitSchema = z.union([z.literal(10), z.literal(50), z.literal(100)]);
export type FleetLogReadLimit = (typeof FLEET_LOG_READ_LIMITS)[number];

export const ReadFleetLogQuerySchema = z.object({
  worker: z.string().min(1).max(128),
  stream: FleetLogLineStreamSchema,
  limit: z.coerce.number().pipe(FleetLogReadLimitSchema).optional(),
  /** Narrows to one round's lines. Absent reads the worker's lines whatever job they belong to. */
  jobId: z.uuid().optional(),
});

export type ReadFleetLogQuery = z.infer<typeof ReadFleetLogQuerySchema>;

export const FleetLogLineViewSchema = z.object({
  at: z.iso.datetime(),
  seq: z.number().int().nonnegative(),
  text: z.string(),
  jobId: z.uuid().nullable(),
});

/**
 * `lines` is returned OLDEST first, the order a log is read in, even though
 * the newest N are what was selected.
 */
export const ReadFleetLogResponseSchema = z.object({
  worker: z.string(),
  stream: FleetLogLineStreamSchema,
  lines: z.array(FleetLogLineViewSchema),
});

export type ReadFleetLogResponse = z.infer<typeof ReadFleetLogResponseSchema>;

/**
 * The machine-signed read behind `fleet-agent agent logs --machine <name>`.
 *
 * Its own schema rather than the operator's: a terminal asks for any count up
 * to a bound, where the dashboard offers three fixed page sizes. `machine`
 * resolves by name OR hostname — a person types the name they see on the fleet
 * page. `worker` is optional: absent reads every worker on that machine that
 * has lines on the stream.
 */
export const FLEET_LOG_MACHINE_READ_MAX_LIMIT = 500;
export const FLEET_LOG_MACHINE_READ_DEFAULT_LIMIT = 10;

export const ReadFleetMachineLogsQuerySchema = z.object({
  machine: z.string().min(1).max(128),
  stream: FleetLogLineStreamSchema,
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(FLEET_LOG_MACHINE_READ_MAX_LIMIT)
    .default(FLEET_LOG_MACHINE_READ_DEFAULT_LIMIT),
  worker: z.string().min(1).max(128).optional(),
});

export type ReadFleetMachineLogsQuery = z.infer<typeof ReadFleetMachineLogsQuerySchema>;

export const ReadFleetMachineLogsResponseSchema = z.object({
  machine: z.string(),
  stream: FleetLogLineStreamSchema,
  workers: z.array(
    z.object({
      worker: z.string(),
      lines: z.array(FleetLogLineViewSchema),
    }),
  ),
});

export type ReadFleetMachineLogsResponse = z.infer<typeof ReadFleetMachineLogsResponseSchema>;

/**
 * How far back a log clean keeps (IMM-918).
 *
 * Fixed rather than a parameter: an operator clearing a worker's logs wants
 * the recent ones kept and the backlog gone, and a free-form cutoff turns a
 * housekeeping button into a way to delete the evidence of the round that is
 * running right now.
 */
export const FLEET_LOG_CLEAN_KEEP_HOURS = 24;

export const CleanFleetLogBodySchema = z.object({
  worker: z.string().min(1).max(128),
});

export type CleanFleetLogBody = z.infer<typeof CleanFleetLogBodySchema>;

export const CleanFleetLogResponseSchema = z.object({
  worker: z.string(),
  /** How many stored lines the clean removed. Zero is a normal answer, not a failure. */
  deleted: z.number().int().nonnegative(),
  keptSince: z.iso.datetime(),
});

export type CleanFleetLogResponse = z.infer<typeof CleanFleetLogResponseSchema>;

/**
 * What the fleet stream says (OPS-26). A doorbell, never a delivery: each event
 * names a KIND of change and nothing else, and the host answers it by ticking at
 * once. The tick stays the only path anything travels down, so a host that
 * misses an event — or never opens the stream — still gets the same answer one
 * interval later.
 *
 * Add a kind when the backend has something new to wake a host for. A host
 * ticks on every event, including a kind it does not know, so a new kind never
 * needs the agent to ship first.
 */
export const FLEET_STREAM_EVENT_KINDS = ['commands-pending', 'assistant-pending'] as const;
export const FleetStreamEventKindSchema = z.enum(FLEET_STREAM_EVENT_KINDS);
export type FleetStreamEventKind = (typeof FLEET_STREAM_EVENT_KINDS)[number];

export const FleetStreamEventSchema = z.object({
  kind: FleetStreamEventKindSchema,
});

export type FleetStreamEvent = z.infer<typeof FleetStreamEventSchema>;
